import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { UtilsService } from 'src/app/services/utils.service';
import { BikeElement } from '../bike';
import { FormBuilder, FormControl } from '@angular/forms';
import { BikeComponent } from '../../component/component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatePipe } from '@angular/common';
import { AccountService } from 'src/app/services/account.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogData } from '../../modals/confirmation-dialog-data';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';

/** Showing bike details:
 * Bike fields: Bike Id, EoL Date, SW Release EoL, Components(Component, EoL Component, Current component)*/

@Component({
  selector: 'app-bike-details',
  templateUrl: './bike-details.component.html',
  styleUrls: ['./bike-details.component.scss'],
  standalone: false
})
export class BikeDetailsComponent implements OnInit {

  public loading = false;
  public selectedBike: BikeElement = {
    BikeId: 0,
    BikeSerial: "0",
    ConfigId: 0,
    EOLDate: "",
    Brand: "0",
    BikeModelName: "0",
    ConfigName: "0",
    ComponentsHistories: [],
    BikeHistories: [],
    ApprovalBike: false,
    ApprovalBikeString: this.languageService.languageJSON.Global_No
  };
  public approvalInit: string = "false";
  public approvalEdit: boolean = false;
  public approvalControl = new FormControl('false');
  public componentList: BikeComponent[] = [];
  public bikeModifyRole: boolean = false;
  private hasAnyActiveRollout: boolean = false;

  constructor(private httpService: HttpService, private route: ActivatedRoute, private snackBar: MatSnackBar,
    public languageService: LanguageService, private router: Router, private datePipe: DatePipe,
    private accountService: AccountService, private matDialog: MatDialog) {
  }

  /** Initialize bike details */
  ngOnInit(): void {
    this.bikeModifyRole = this.accountService.hasBikeModifyRole();
    this.route.queryParams.subscribe((params) => {
      this.initBike(params['serial']);
    });
  }

  /** Getting bike details from Api endpoint */
  private initBike(bikeId: string): void {
    this.loading = true;

    this.httpService.getBikeDetails(bikeId)
      .subscribe((resp: any) => {

        this.loading = false;
        if (resp.success) {
          const el = resp.data;
          this.selectedBike = {
            BikeId: el.id,
            BikeSerial: el.serial_id,
            ConfigId: el.configuration_id,
            EOLDate: this.datePipe.transform(new Date(el.eol_date), "long") || "",
            Brand: el.brand,
            BikeModelName: el.bike_model_name,
            ConfigName: el.config_name,
            ApprovalBike: el.approval ? true : false,
            ApprovalBikeString: el.approval ? this.languageService.languageJSON.Global_Yes : this.languageService.languageJSON.Global_No,
            ComponentsHistories: this.getComponentHistories(el.components),
            BikeHistories: el.bike_histories ? el.bike_histories.map((bike: any) => {
              return {
                Source: bike.source, Date: new Date(bike.created_at), User: bike.user_id, Action: this.getLogAction(bike.event),
                Details: this.getLogDetails(bike.event)
              }
            }) : []
          }

          this.approvalControl.setValue(this.selectedBike.ApprovalBike.toString());
          this.approvalInit = this.selectedBike.ApprovalBike.toString();
        }
      }, (error) => {
        this.loading = false;
      });
  }

  private getComponentHistories(componentsHistories: any): { Component: string, Filename: string, Version: string, RolloutId: string, ReleaseId: number }[] {
    return componentsHistories.map((component: any) => {
      if (component.releases.length > 0) {
        return {
          Component: component.component_name, Filename: component.releases[0].filename,
          Version: component.releases[0].release_version, RolloutId: component.releases[0].rollouts[0]?.rollout_id,
          ReleaseId: component.releases[0].release_id
        }
      }
      else {
        return {
          Component: component.component_name, Filename: "N/A",
          Version: "N/A", RolloutId: "", ReleaseId: null
        }
      }
    });
  }

  public openRelease(releaseId: number) {
    this.router.navigate(['/view-software'], { queryParams: { id: releaseId } });
  }

  public editApproval() {
    this.approvalEdit = true;
  }

  public saveApproval() {
    this.approvalEdit = false;
    const approvalValue: number = this.approvalControl.value == "true" ? 1 : 0;

    if (approvalValue == 0 && this.hasAnyActiveRollout) {
      const data: ConfirmationDialogData = {
        title: this.languageService.languageJSON.BikeDetail_ConfirmApprovalWarning_Title,
        message: this.languageService.languageJSON.BikeDetail_ConfirmApprovalWarning_Desc,
        buttons: [this.languageService.languageJSON.Global_Yes, this.languageService.languageJSON.Global_No],
      };

      this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
        if (result) {
          this.loading = true;
          this.httpService.addApprovalBike({ bike_id: this.selectedBike.BikeSerial.toString(), approval: approvalValue.toString() }).subscribe((resp: any) => {
            this.loading = false;
            if (resp.success) {
              this.snackBar.open(this.languageService.languageJSON.Global_ChangesSaved, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.ngOnInit();
            }
          });
        }
      });
    }
    else {
      this.loading = true;
      this.httpService.addApprovalBike({ bike_id: this.selectedBike.BikeSerial.toString(), approval: approvalValue.toString() }).subscribe((resp: any) => {
        this.loading = false;
        if (resp.success) {
          this.snackBar.open(this.languageService.languageJSON.Global_ChangesSaved, undefined, { panelClass: 'success-snackbar', duration: 5000 });
          this.ngOnInit();
        }
      });
    }
  }

  private getLogAction(logId: string): string {
    return this.languageService.languageJSON[`ERROR_${logId}_Action`];
  }

  private getLogDetails(logId: string): string {
    return this.languageService.languageJSON[`ERROR_${logId}_Details`];
  }

  public isFormChanged(): boolean {
    return this.approvalEdit && this.approvalInit != this.approvalControl.value;
  }
}
